import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';
import { createPart, deletePart } from '../../graphql/mutations';
import { AppContext } from '../../App';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  CircularProgress,
  Paper,
  Container,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Zoom
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchPricelist from '../search/SearchPricelist';
import AdminView from '../AdminView';

const getBoxWithParts = `
  query GetBox($id: ID!) {
    getBox(id: $id) {
      id
      name
      status
      parts {
        items {
          id
          partNumber
          price
          grade
        }
      }
    }
  }
`;

const BoxDetails = () => {
  const { invoiceId, boxId, providerName } = useParams();
  const navigate = useNavigate();
  const [box, setBox] = useState(null);
  const [loading, setLoading] = useState(true);
  const { userRecord } = useContext(AppContext);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partToDelete, setPartToDelete] = useState(null);

  const fetchBox = async () => {
    setLoading(true);
    try {
      // Use the new query
      const response = await API.graphql(
        graphqlOperation(getBoxWithParts, { id: boxId })
      );
      console.log('Box data:', response.data.getBox); // Add this for debugging
      setBox(response.data.getBox);
    } catch (error) {
      console.error('Error fetching box:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBox();
  }, [boxId]);

  const handleAddPart = async (part, grade) => {
    try {
      const resolvedPrice = parseFloat(
        (part && (
          part.groupAdjustedPrice ??
          part.adjustedPrice ??
          part["PRICE"] ??
          part.price ??
          part.minPrice
        )) || 0
      );
      const finalPrice = isNaN(resolvedPrice)
        ? 0
        : Math.round(resolvedPrice * (parseFloat(grade) === 0.5 ? 0.5 : 1));
      const partToAdd = {
        partNumber: part.partNumber,
        price: finalPrice,
        fromPricelist: userRecord.activePricelistID,
        grade: parseFloat(grade),
        boxPartsId: boxId
      };

      await API.graphql(
        graphqlOperation(createPart, { input: partToAdd })
      );

      fetchBox(); // Refresh the box data after adding a part
    } catch (error) {
      console.error('Error adding part:', error);
    }
  };

  const handleDeletePart = async () => {
    try {
      await API.graphql(
        graphqlOperation(deletePart, {
          input: { id: partToDelete.id }
        })
      );
      setDeleteDialogOpen(false);
      setPartToDelete(null);
      fetchBox();
    } catch (error) {
      console.error('Error deleting part:', error);
    }
  };

  const handleBackToInvoice = () => {
    if (providerName) {
      navigate(`/${providerName}/view-invoice/${invoiceId}`);
    } else {
      navigate(`/view-invoice/${invoiceId}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!box) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToInvoice}
          variant="outlined"
          sx={{ mb: 3 }}
        >
          Back to Invoice
        </Button>
        <Typography>Box not found</Typography>
      </Box>
    );
  }

  const content = (
    <Container maxWidth="md" style={{marginTop: 50}}>
      <Box sx={{ py: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToInvoice}
          variant="outlined"
          sx={{ mb: 3 }}
        >
          Back to Invoice
        </Button>

        <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }} style={{marginTop: 20}}>
          {box?.name}
        </Typography>

        {/* Search Section */}
        
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Add Parts to Box
          </Typography>
          <SearchPricelist
              onPartSelect={handleAddPart}
              showPricelistSelect={false}
              showDisclaimers={false}
          />

        {/* Parts List */}
        <Paper 
          elevation={3}
          style={{padding: 15}}
          sx={{ 
            p: 3,
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.9)'
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
            Parts in Box ({box?.parts?.items?.length || 0})
          </Typography>
          <List>
            {box?.parts?.items?.length > 0 ? (
              box.parts.items.map((part) => (
                <Zoom in={true} key={part.id}>
                  <ListItem 
                    sx={{
                      border: '1px solid #eee',
                      borderRadius: '12px',
                      mb: 2,
                      backgroundColor: 'white',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateX(8px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        sx={{ 
                          bgcolor: 'primary.main',
                          fontWeight: 'bold'
                        }}
                      >
                        {part.grade === 1 ? "1" : "½"}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {part.partNumber}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                          ${part.price?.toFixed(2) || "0.00"}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Delete Part" placement="left">
                        <IconButton 
                          edge="end" 
                          onClick={() => {
                            setPartToDelete(part);
                            setDeleteDialogOpen(true);
                          }}
                          sx={{
                            '&:hover': {
                              color: 'error.main'
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Zoom>
              ))
            ) : (
              <Typography 
                sx={{ 
                  textAlign: 'center', 
                  py: 4, 
                  color: 'text.secondary',
                  fontStyle: 'italic'
                }}
              >
                No parts added to this box yet
              </Typography>
            )}
          </List>
        </Paper>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setPartToDelete(null);
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete part {partToDelete?.partNumber}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setPartToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeletePart} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );

  return providerName ? (
    <AdminView>{content}</AdminView>
  ) : (
    content
  );
};

export default BoxDetails; 